import React, { Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import uniqueId from 'lodash.uniqueid'
import { matchSorter } from 'match-sorter'
import {
  useCombobox,
  useMultipleSelection,
  UseMultipleSelectionReturnValue,
  UseMultipleSelectionStateChange,
} from 'downshift'
import { ListChildComponentProps, VariableSizeList } from 'react-window'
import { Combobox as ComboboxStyles } from '@airbus/elements/elements'
import { UnfoldMore, Close, UnfoldLess } from '@mui/icons-material'
import useControlled from '../../lib/hooks/useControlled'
import useDeprecatedComponent from '../../lib/hooks/useDeprecatedComponent'
import useGetLatest from '../../lib/hooks/useGetLatest'
import { createUseStyles } from '../../lib/styling'
import IconButton from '../IconButton'
import List from '../List'
import Popover from '../Popover'
import { CommonProps } from '../helperTypes'
import {
  ComboBoxOption,
  ComboBoxGetOptionProperty,
  ComboBoxGroupedOption,
  ComboBoxVirtualizationOptions,
  getOptionLabelDefault,
  getOptionValueDefault,
  getOptionDisabled,
  isOptionSelected,
  OPTION_TYPE_CREATE,
} from './helper/utils'
import { comboboxStateController, mutipleSelectionStateController } from './helper/stateController'
import { comboboxStateReducer } from './helper/stateReducer'
import ComboBoxContext from './ComboBoxContext'
import MultipleTags from './MultipleTags'
import RowOption from './RowOption'

const ROW_HEIGHT = 50
const UNCONTROLLED_DEFAULT_OPTIONS: any = []

type Props<T> = {
  /**
   * Enable virtualization for huge lists.
   * Use it with `virtualizationOptions` prop.
   * You must set a fixed height when using this option.
   * (See <a href="#virtualized" target="_self">Virtualized</a>)
   * @default false
   */
  allowVirtualization?: boolean
  /**
   * Allow creation of new options.
   * (See <a href="#creatable" target="_self">Creatable</a>)
   * @default false
   */
  creatable?: boolean
  /**
   * Useful to control the initial text displayed in input element.
   * (See <a href="#default-input-value" target="_self">Default input value</a>)
   */
  defaultInputValue?: string
  /**
   * default value used initially
   * @type string | string[] | T | T[] | null
   */
  defaultValue?: string | T | T[] | null
  /**
   * If true, input will be disabled
   * @default false
   */
  disabled?: boolean
  /**
   * Allow to switch the [matching algorithm](https://github.com/kentcdodds/match-sorter) used when typing text to filter options
   * (See <a href="#filter-match" target="_self">Filter Match</a>)
   * @default CONTAINS
   */
  filterMatch?:
    | 'MATCHES'
    | 'ACRONYM'
    | 'CONTAINS'
    | 'WORD_STARTS_WITH'
    | 'STARTS_WITH'
    | 'EQUAL'
    | 'CASE_SENSITIVE_EQUAL'
  /**
   * Implement this getter if you want to customize selected option labels in multiple mode
   * @default (option) => typeof option === "string" ? option : option.label;
   **/
  getChipLabel?: ComboBoxGetOptionProperty<T>
  /**
   * Implement this getter if you provide a specific options structure
   * @default (option) => typeof option === "string" ? option : option.label;
   **/
  getOptionLabel?: ComboBoxGetOptionProperty<T>
  /**
   * Implement this getter if you provide a specific options structure
   * @default (option) => typeof option === "string" ? option : option.value;
   **/
  getOptionValue?: ComboBoxGetOptionProperty<T>
  /**
   * Use this method to define group of options.<br />
   * Cannot be used with virtualization.<br />
   * The string returned for each option will determine the group names.
   * @returns group name
   */
  groupBy?: (option: T) => string
  /**
   * Hide global clear icon button
   * @default false
   */
  hideClear?: boolean
  /**
   * Hide clear icon button on disabled options
   * (See <a href="#locked-options" target="_self">Locked Options</a>)
   * @default false
   **/
  hideClearOnDisabled?: boolean
  /**
   * Enable invalid/error visual status
   * @default false
   **/
  invalid?: boolean
  /**
   * Option to keep input text on select in multiple mode
   * (See <a href="#default-input-value" target="_self">Default input value</a>)
   * @default false
   */
  keepInputOnSelect?: boolean
  /**
   * The maximum number of chips that will be visible. Set -1 to disable the limit.
   * (See <a href="#limit-size" target="_self">Limit Size</a>)
   * @default -1
   */
  limitChips?: number
  /**
   * if true, enable selecting multiple values
   * @default false
   */
  multiple?: boolean
  /**
   * The callback called when using creatable mode.
   * (See <a href="#creatable" target="_self">Creatable</a>)
   */
  onCreate?: (value: string) => void
  /**
   * Array of options.
   * (See <a href="#custom-options-and-labels" target="_self">Custom Options</a>)
   * @type string[] | T[]
   */
  options?: T[]
  /** The text to display when no option has been selected */
  placeholder?: string
  /**
   * Custom content when input value does not match any options
   * @default "No item matching"
   */
  renderNoMatch?: () => ReactNode
  /**
   * The size of the input
   * @default medium
   */
  size?: 'small' | 'medium'
  /**
   * Enable valid/success visual status
   * @default false
   **/
  valid?: boolean
  /**
   * controlled value
   * @type string | string[] | T | T[] | null
   */
  value?: string | T | T[] | null
  /**
   * Virtualization Options.
   * (See <a href="#virtualized" target="_self">Virtualized</a>)
   * @default { rowsVisible: 10, rowHeight: 40, overscanCount: 5 }
   */
  virtualizationOptions?: ComboBoxVirtualizationOptions
} & (
  | {
      multiple?: false
      defaultValue?: T | string | null
      value?: T | string | null
      /** The callback for when the selected value changes */
      onChange?: (value: T) => void
    }
  | {
      multiple?: true
      defaultValue?: T[] | string[] | null
      value?: T[] | string | null
      onChange?: (value: T[]) => void
    }
)

export type ComboBoxProps<T extends ComboBoxOption> = Props<T> & CommonProps

const useStyles = createUseStyles(ComboboxStyles, 'combobox')

const ComboBox = <T extends ComboBoxOption>(props: ComboBoxProps<T>) => {
  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    className,
    creatable = false,
    defaultInputValue = '',
    defaultValue,
    disabled,
    filterMatch = 'CONTAINS',
    getChipLabel = getOptionLabelDefault,
    getOptionLabel = getOptionLabelDefault,
    getOptionValue = getOptionValueDefault,
    onInputValueChange,
    hideClear,
    hideClearOnDisabled = false,
    groupBy,
    id: userId,
    invalid,
    keepInputOnSelect,
    limitChips = -1,
    multiple = false,
    onChange,
    onCreate,
    options: optionsProp,
    placeholder,
    renderNoMatch,
    size,
    style,
    valid,
    value: valueProp,
    allowVirtualization = false,
    virtualizationOptions = {},
    ...rest
  } = props
  const [rootNode, setRootNode] = useState<HTMLDivElement | null>(null)
  const idRef = useRef<string>(uniqueId('combobox-'))
  const id = userId || idRef.current
  const virtualizedListRef = useRef<VariableSizeList>(null)
  const [inputValue, setInputValue] = useState<string | undefined>(defaultInputValue)
  const classes = useStyles()

  // controlled/uncontrolled state
  const [value, setValueState] = useControlled({
    value: valueProp,
    defaultValue,
  })
  const [options, setOptions] = useControlled({
    value: optionsProp,
    defaultValue: UNCONTROLLED_DEFAULT_OPTIONS,
  })

  // latest ref hook pattern to reduce new closure creation on each render
  const getLatest = useGetLatest(
    {
      getChipLabel,
      getOptionLabel,
      getOptionValue,
      multiple,
      onChange,
      options,
    },
    [options],
  )

  // reset inputValue when controlled value is cleared
  useEffect(() => {
    if (
      defaultInputValue === '' &&
      (valueProp === null || valueProp === '' || (valueProp && Object.keys(valueProp).length === 0))
    ) {
      setInputValue(defaultInputValue)
    }
  }, [valueProp, defaultInputValue])

  const onSelectedItemsChange = React.useCallback(
    ({ selectedItems = [] }: UseMultipleSelectionStateChange<T>) => {
      const { multiple, onChange } = getLatest()
      const newValue: any = multiple ? selectedItems : selectedItems[0] || ''
      setValueState(newValue)
      if (onCreate) {
        if (!multiple && newValue.__type === OPTION_TYPE_CREATE) return
        if (multiple && newValue.length > 0 && newValue[newValue.length - 1].__type === OPTION_TYPE_CREATE) return
      }
      onChange && onChange(newValue)
    },
    [getLatest, onCreate, setValueState],
  )
  // initialize selectedItems state from optional value prop
  // if defined and if the value(s) exist in items
  // FIXME this memo hook leads to too much render loops
  // FIXME this whole component could be refactored with a reducer hook maybe
  const initialSelectedItems = useMemo(() => {
    const { getOptionLabel, getOptionValue, multiple, options } = getLatest()
    if (!value || options.length === 0) return []
    if (!multiple) {
      // single mode value (string or object)
      const item = options.find(
        (option: T) => getOptionValue(option) === value || getOptionLabel(option) === getOptionLabel(value),
      )
      if (item) setInputValue(getOptionLabel(item))
      return item ? [item] : []
    } else {
      if (!Array.isArray(value)) return []
      // multiple mode value (array)
      return options.filter((option: T) => isOptionSelected(value, option, getOptionLabel))
    }
  }, [getLatest, value])

  // useMultipleSelection hook for multiple mode
  // https://www.downshift-js.com/use-multiple-selection
  const multipleSelectionValues: UseMultipleSelectionReturnValue<T> = useMultipleSelection<T>({
    itemToString: getOptionValue,
    onSelectedItemsChange,
    selectedItems: initialSelectedItems,
    onStateChange: (changes) => mutipleSelectionStateController(changes, limitChips, reset),
  })
  const { getDropdownProps, removeSelectedItem, reset, selectedItems, setSelectedItems, addSelectedItem } =
    multipleSelectionValues

  // set filtering flag when we have an inputValue different from selected item
  const isFiltering = React.useMemo(() => {
    const hasInputValue = inputValue !== undefined && inputValue !== ''
    if (!hasInputValue) return false
    if (multiple) return true
    const selectedItemLabel = selectedItems.length > 0 ? getOptionLabel(selectedItems[0]) : ''
    return selectedItemLabel !== inputValue
  }, [getOptionLabel, inputValue, multiple, selectedItems])

  // check need to display creatable custom option
  const creatableOption = useMemo(() => {
    const { getOptionLabel, options } = getLatest()
    if (!creatable || !inputValue || !isFiltering) return null
    const exactMatch = matchSorter(options, inputValue, {
      keys: [getOptionLabel],
      threshold: matchSorter.rankings.EQUAL,
    })
    if (!exactMatch.length) {
      // internal __type value used for "special" options
      return { value: inputValue, __type: OPTION_TYPE_CREATE }
    }
  }, [creatable, getLatest, inputValue, isFiltering])

  // - filter list of options when typing
  // - compute grouped options when using groupBy
  const filteredOptions = useMemo(() => {
    const { getOptionLabel, options } = getLatest()
    let filtered: T[] = options
    const grouped: ComboBoxGroupedOption<T>[] = []

    // filter default options with typed input
    if (inputValue && isFiltering) {
      filtered = matchSorter(options, inputValue, {
        keys: [getOptionLabel],
        threshold: matchSorter.rankings[filterMatch],
      }).filter((item: any) => selectedItems.indexOf(item) < 0) as T[]
      if (creatableOption) filtered.push(creatableOption as any)
    }

    // grouped options when using groupBy
    if (groupBy) {
      const indexByGroup = new Map()
      filtered.forEach((option: T) => {
        const groupName = groupBy(option)
        const groupIndex = indexByGroup.get(groupName)
        if (groupIndex >= 0) {
          grouped[groupIndex].options.push(option)
        } else {
          indexByGroup.set(groupName, grouped.length)
          grouped.push({ group: groupName, options: [option] })
        }
      })
      // remap filtered options by groups
      filtered = grouped.flatMap((group) => group.options)
    }

    return {
      filtered,
      grouped,
    }
  }, [creatableOption, filterMatch, getLatest, groupBy, inputValue, selectedItems, isFiltering])

  // find first enabled option to highlight by default
  const defaultHighlightedIndex: number = useMemo(() => {
    return filteredOptions.filtered.findIndex((option: T) => !getOptionDisabled(option))
  }, [filteredOptions])

  // useCombobox hook for full accessible state
  // https://www.downshift-js.com/use-combobox
  const { isOpen, getToggleButtonProps, getMenuProps, getInputProps, getItemProps } = useCombobox<T>({
    id,
    inputId: id,
    inputValue,
    defaultHighlightedIndex,
    selectedItem: null,
    items: filteredOptions.filtered,
    onInputValueChange,
    isItemDisabled: (item, _index) => getOptionDisabled(item),
    stateReducer: (state, changeOptions) =>
      comboboxStateReducer<T>(
        state,
        changeOptions,
        creatable,
        filteredOptions.filtered,
        inputValue,
        defaultInputValue,
        multiple,
      ),
    // onStateChange used to customize useCombobox state for single/multiple mode and controlled state
    // @see https://github.com/downshift-js/downshift/tree/master/src/hooks/useCombobox#onstatechange
    onStateChange: (changes) =>
      comboboxStateController<T>(
        changes,
        multipleSelectionValues,
        allowVirtualization,
        getOptionLabel,
        isOpen,
        isOptionSelected,
        keepInputOnSelect,
        options,
        multiple,
        onCreate,
        setInputValue,
        setOptions,
        virtualizedListRef.current,
      ),
  })

  const renderOptions = () => {
    if (!groupBy) {
      return filteredOptions.filtered.map((item: T, index: number) => {
        return <RowOption item={item} index={index} key={`${getOptionValue(item as T)}${index}`} />
      })
    } else {
      return filteredOptions.grouped.map((item: ComboBoxGroupedOption<T>, index: number) => (
        <Fragment key={`group-${item.group}${index}`}>
          <RowOption isGroup item={item} index={index} />
          {item.options.map((option: T, optIndex: number) => (
            <RowOption
              isNested
              item={option}
              index={filteredOptions.filtered.findIndex((opt: T) => opt === option)}
              key={`${getOptionValue(option)}${optIndex}`}
            />
          ))}
        </Fragment>
      ))
    }
  }

  // renders when virtualization is enabled
  const renderVirtualizedOptions = () => {
    const optionSize = filteredOptions.filtered.length
    const {
      rowsVisible = optionSize < 10 ? optionSize : 10,
      rowHeight = ROW_HEIGHT,
      overscanCount = 5,
    } = virtualizationOptions
    // compute virtualized list height corresponding to "maxHeight: 40vh"
    const viewportHeight = Math.max(
      document.documentElement.clientHeight,
      typeof window !== 'undefined' ? window.innerHeight : 0,
    )
    const listHeight = Math.min(rowsVisible * ROW_HEIGHT, (40 * viewportHeight) / 100)
    return (
      <VariableSizeList
        height={listHeight}
        itemCount={optionSize}
        itemSize={() => rowHeight}
        width={'100%'}
        overscanCount={overscanCount}
        ref={virtualizedListRef}
      >
        {(props: ListChildComponentProps) => {
          const item = filteredOptions.filtered[props.index]
          return (
            <RowOption
              item={item}
              index={props.index}
              key={`${getOptionValue(item)}${props.index}`}
              style={props.style}
            />
          )
        }}
      </VariableSizeList>
    )
  }

  // dropdown keydown handler is needed for keyboard accessibility
  const { onKeyDown: onDropdownKeyDown, ...dropdownRest } = getDropdownProps({}, { suppressRefError: true })

  return (
    <ComboBoxContext.Provider
      value={{
        classes,
        filteredOptions,
        getChipLabel,
        getItemProps,
        getOptionDisabled,
        getOptionLabel,
        getOptionValue,
        hideClearOnDisabled,
        isOptionSelected,
        limitChips,
        multiple,
        options,
        removeSelectedItem,
        selectedItems,
        setSelectedItems,
        addSelectedItem,
      }}
    >
      <div
        onMouseDown={(event) => {
          // Prevent input blur when interacting with the combobox
          if ((event.target as HTMLElement).getAttribute('id') !== id) {
            event.preventDefault()
          }
        }}
      >
        <div
          ref={(node) => setRootNode(node)}
          className={clsx(className, classes.root, {
            [classes._disabled]: disabled,
            [classes._invalid]: invalid,
            [classes._noclear]: hideClear,
            [classes._small]: size === 'small',
            [classes._valid]: valid,
          })}
          style={style}
        >
          {multiple && <MultipleTags />}
          <input
            className={classes.input}
            type='text'
            {...getInputProps(
              {
                ...dropdownRest,
                disabled,
                placeholder,
                onKeyDown: (evt: any) => {
                  if (evt.key === 'Escape') {
                    // prevent closing dialogs when pressing ESC
                    if (isOpen) evt.stopPropagation()
                  }
                  onDropdownKeyDown && onDropdownKeyDown(evt)
                },
                // remove "aria-controls" attribute when menu is closed
                // Needed for https://dequeuniversity.com/rules/axe/4.5/aria-valid-attr-value?application=axeAPI
                ...(!isOpen && { 'aria-controls': undefined }),
                // downshift enforces aria-labelledby by default through getLabelProps API
                // we don't provide label element so it results in invalid aria attributes
                // workaround: disable aria-labelledby attribute if not provided
                'aria-labelledby': ariaLabelledby ? ariaLabelledby : undefined,
                'aria-label': !ariaLabelledby ? ariaLabel || placeholder : undefined,
              },
              { suppressRefError: true },
            )}
            {...rest}
          />
          <div className={classes.adornment}>
            {!hideClear && value && (!multiple || (multiple && value.length > 0)) && (
              <IconButton
                aria-label='clear input'
                className={classes.clearBtn}
                size={size === 'small' ? 'xsmall' : 'small'}
                tabIndex={-1}
                variant='ghost'
                onClick={(evt) => {
                  evt.stopPropagation()
                  setInputValue('')
                  reset()
                }}
              >
                <Close />
              </IconButton>
            )}
            <IconButton
              aria-label='toggle menu'
              size={size === 'small' ? 'xsmall' : 'small'}
              variant='ghost'
              {...getToggleButtonProps({ disabled })}
            >
              {isOpen ? <UnfoldLess /> : <UnfoldMore />}
            </IconButton>
          </div>
        </div>

        <Popover
          arrow={false}
          disableRoles
          offset={[0, 2]}
          open={isOpen}
          className={classes.popover}
          referenceNode={rootNode}
          style={{
            width: rootNode ? rootNode.clientWidth : undefined,
          }}
        >
          {filteredOptions.filtered.length === 0 && (
            <div className={classes.nomatch}>{renderNoMatch ? renderNoMatch() : 'No item matching'}</div>
          )}
          <List className={classes.menu} {...getMenuProps({}, { suppressRefError: true })}>
            {allowVirtualization ? renderVirtualizedOptions() : renderOptions()}
          </List>
        </Popover>
      </div>
    </ComboBoxContext.Provider>
  )
}

export default ComboBox

// retrocompat : Deprecated "Typeahead" component
export const Typeahead = <T extends ComboBoxOption>(props: ComboBoxProps<T>) => {
  useDeprecatedComponent('Typeahead', 'Please use ComboBox.')
  return <ComboBox {...props} />
}
